import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import NotificationToast from './NotificationToast';
import { useNotifications } from '../contexts/NotificationContext';
import './NotificationToast.css';

const NotificationContainer = () => {
  const { notifications, dismissNotification, removeNotification } = useNotifications();
  const timeoutRefs = useRef({});
  const [exitingIds, setExitingIds] = useState(new Set());

  // Filter out dismissed notifications from toast display
  const visibleNotifications = notifications.filter((n) => !n.dismissed && !exitingIds.has(n.id));

  useEffect(() => {
    // Auto-hide completed notifications after 5 seconds
    visibleNotifications.forEach((notification) => {
      if (notification.status === 'completed' && notification.dismissible) {
        // Clear existing timeout if any
        if (timeoutRefs.current[notification.id]) {
          clearTimeout(timeoutRefs.current[notification.id]);
        }
        
        // Set new timeout to auto-dismiss after 5 seconds
        timeoutRefs.current[notification.id] = setTimeout(() => {
          setExitingIds((prev) => new Set([...prev, notification.id]));
          setTimeout(() => {
            dismissNotification(notification.id);
            setExitingIds((prev) => {
              const next = new Set(prev);
              next.delete(notification.id);
              return next;
            });
          }, 300); // Wait for animation to complete
          delete timeoutRefs.current[notification.id];
        }, 5000);
      }
    });

    // Cleanup function
    return () => {
      Object.values(timeoutRefs.current).forEach((timeout) => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, [visibleNotifications, dismissNotification]);

  const handleDismiss = (id) => {
    setExitingIds((prev) => new Set([...prev, id]));
    setTimeout(() => {
      dismissNotification(id);
      setExitingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 300);
  };

  const handleRemove = (id) => {
    setExitingIds((prev) => new Set([...prev, id]));
    setTimeout(() => {
      removeNotification(id);
      setExitingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 300);
  };

  return (
    <div className="notification-container">
      <AnimatePresence mode="popLayout">
        {visibleNotifications.map((notification) => (
          <motion.div
            key={notification.id}
            initial={{ opacity: 0, x: 100, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 100, scale: 0.95 }}
            transition={{ 
              duration: 0.3,
              ease: [0.4, 0, 0.2, 1]
            }}
            layout
          >
            <NotificationToast
              notification={notification}
              onDismiss={handleDismiss}
              onRemove={handleRemove}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default NotificationContainer;
